import { useState, useEffect, useRef, useCallback } from 'react';
import type {
  BulkConversionJob,
  BulkConversionJobStatus,
  BulkConversionResult,
  BulkConversionResultStatus,
} from '../MasterDocument.types';
import { resolveCrossingError } from './crossingFetch';
import { useMasterDocumentTranslation } from '../i18n';
import type { CrossingFetch } from './crossingFetch';

interface UseBulkConversionOptions {
  /**
   * Salida HTTP del bulk-convert. Enruta por `adapter.genericFetch` (auth,
   * baseUrl, remapeo de rutas) — ver {@link createCrossingFetch}.
   */
  crossingFetch: CrossingFetch;
}

interface UseBulkConversionReturn {
  job: BulkConversionJob | null;
  showResults: boolean;
  lastClassId: string | null;
  error: string | undefined;
  startJob: (
    classId: string,
    documentIds: string[],
    headerValues?: Record<string, unknown>,
  ) => Promise<void>;
  cancelJob: () => Promise<void>;
  closeProgress: () => void;
  openResults: () => void;
  closeResults: () => void;
  retryFailed: (failedIds: string[]) => Promise<void>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeResultStatus(raw: any): BulkConversionResultStatus {
  // Acepta el status textual del backend (Successful/Partial/Failed) o el
  // booleano `success` legado.
  const s = String(raw.status ?? '').toLowerCase();
  if (s === 'partial') return 'partial';
  if (s === 'failed' || s === 'error') return 'failed';
  if (s === 'successful' || s === 'success' || s === 'completed') return 'success';
  if (typeof raw.success === 'boolean') return raw.success ? 'success' : 'failed';
  // Sin status explícito: si hay líneas bloqueadas pero se generó hijo → partial.
  const blocked = raw.blocked_lines ?? raw.blockedLines;
  if (Array.isArray(blocked) && blocked.length > 0 && (raw.generated_document_id ?? raw.child_document_id)) {
    return 'partial';
  }
  return 'failed';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapResult(raw: any): BulkConversionResult {
  return {
    sourceDocumentId: raw.source_document_id ?? raw.parent_document_id,
    sourceDocumentNumber: raw.source_document_number,
    status: normalizeResultStatus(raw),
    childDocumentId: raw.generated_document_id ?? raw.child_document_id,
    childDocumentNumber: raw.generated_document_number,
    blockedLines: raw.blocked_lines ?? raw.blockedLines,
    errorCode: raw.error_code ?? raw.errorCode,
    errorMessage: raw.error ?? raw.error_reason ?? raw.errorReason,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeJobStatus(raw: any): BulkConversionJobStatus {
  const s = String(raw ?? '').toLowerCase();
  if (s === 'pending') return 'pending';
  if (s === 'completed' || s === 'complete') return 'completed';
  if (s === 'failed') return 'failed';
  if (s === 'cancelled' || s === 'canceled') return 'cancelled';
  return 'running';
}

/** Estados terminales: el polling se detiene al alcanzarlos. */
function isTerminalJobStatus(status: BulkConversionJobStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

export function useBulkConversion({ crossingFetch }: UseBulkConversionOptions): UseBulkConversionReturn {
  // Traduce los errores que genera el kit; los que trae el backend pasan intactos.
  const { t } = useMasterDocumentTranslation();
  const [job, setJob] = useState<BulkConversionJob | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [lastClassId, setLastClassId] = useState<string | null>(null);
  const [lastHeaderValues, setLastHeaderValues] = useState<Record<string, unknown> | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const pollJob = useCallback(async (jobId: string) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = await crossingFetch<any>(
        'GET',
        `/api/v1/crossing/jobs/${encodeURIComponent(jobId)}`,
      );
      const updated: BulkConversionJob = {
        jobId: payload.job_id,
        status: normalizeJobStatus(payload.status),
        total: payload.total,
        processed: payload.processed,
        results: (payload.results ?? []).map(mapResult),
      };
      setJob(updated);
      if (isTerminalJobStatus(updated.status)) {
        stopPolling();
      }
    } catch {
      // El polling es transitorio: un fallo puntual no detiene el seguimiento
      // (el siguiente tick reintenta). Los fallos terminales se ven al cancelar.
    }
  }, [crossingFetch, stopPolling]);

  const startJob = useCallback(async (
    classId: string,
    documentIds: string[],
    headerValues?: Record<string, unknown>,
  ) => {
    stopPolling();
    setShowResults(false);
    setError(undefined);
    setLastClassId(classId);
    setLastHeaderValues(headerValues);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const body: any = { document_ids: documentIds, child_class_id: classId };
      // Header común (FE-07): solo se envía si el consumidor lo provee.
      if (headerValues && Object.keys(headerValues).length > 0) {
        body.header_values = headerValues;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = await crossingFetch<any>('POST', '/api/v1/crossing/bulk-convert', body);
      const newJob: BulkConversionJob = {
        jobId: payload.job_id,
        status: 'running',
        total: payload.total,
        processed: 0,
        results: [],
      };
      setJob(newJob);
      intervalRef.current = setInterval(() => {
        void pollJob(payload.job_id);
      }, 3000);
    } catch (err) {
      setError(resolveCrossingError(err, t).message);
    }
  }, [crossingFetch, stopPolling, pollJob, t]);

  const cancelJob = useCallback(async () => {
    if (!job) return;
    stopPolling();
    try {
      await crossingFetch('DELETE', `/api/v1/crossing/jobs/${encodeURIComponent(job.jobId)}`);
      setJob((prev) => prev ? { ...prev, status: 'cancelled' } : null);
    } catch (err) {
      setError(resolveCrossingError(err, t).message);
    }
  }, [job, crossingFetch, stopPolling, t]);

  const closeProgress = useCallback(() => {
    stopPolling();
    setJob(null);
    setShowResults(false);
    setError(undefined);
  }, [stopPolling]);

  const openResults = useCallback(() => setShowResults(true), []);
  const closeResults = useCallback(() => setShowResults(false), []);

  const retryFailed = useCallback(async (failedIds: string[]) => {
    if (!lastClassId) return;
    await startJob(lastClassId, failedIds, lastHeaderValues);
  }, [lastClassId, lastHeaderValues, startJob]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  return { job, showResults, lastClassId, error, startJob, cancelJob, closeProgress, openResults, closeResults, retryFailed };
}
